import { gitWriteLockSelector } from "containers/Resources/Resources.selectors";
import { RecordType } from "graphql/generated/graphql";
import { useMemo } from "react";
import { useSelector } from "react-redux";

export interface GitWriteLock {
    state: "WRITE_WAIT" | "WRITE_START" | "WRITE_SUCCEEDED" | "WRITE_FAILED";
    record: {
        type: RecordType;
        id: string;
    };
    lockfile: string;
    exception?: { title: string; message: string };
    // Whether the git write was initiated by an interactive Console user rather
    // than a system/background actor (a running flow or an internal
    // engine/git-sync call). Emitted by the backend git_write_lock pg_notify
    // payload. When false, a WRITE_FAILED is a system-initiated failure that the
    // interactive user did not cause and must not be toasted to them. Absent on
    // payloads from older backends -> treated as interactive (notify), preserving
    // prior behaviour.
    interactive?: boolean;
    // Names the high-level git operation this lock guards: "commit" for the
    // single-record Commit action and "bulk_commit" for the container Bulk
    // commit action in the Console. Emitted by the backend
    // git_write_lock pg_notify payload. Absent/null for the frequent background
    // auto-commits (every tracked-record save), which must NOT raise a
    // completion toast. See
    // operator-task-fe-commit-completion-signal-graphql-broadcast.
    operation?: string | null;
    // Identity id of the actor that initiated this git write. Emitted by the
    // backend git_write_lock pg_notify payload. The write lock is
    // workspace-global and WRITE_SUCCEEDED is broadcast to EVERY connected
    // client, so the "Commit finished" success toast is shown only when
    // initiated_by matches the current user's identity -- scoping it to the
    // session that fired the commit instead of toasting everyone (e.g. the
    // operator's continuous brain-CO commits). Absent on payloads from older
    // backends -> no match -> no toast (safe: only the committer is toasted).
    // See operator-task-fe-commit-finished-toast-scope-to-initiating-session.
    initiated_by?: string | null;
}

interface GitLockData {
    [id: string]: GitWriteLock;
}

export const useGitWriteLock = () => {
    const gitWriteLock: GitWriteLock[] = useSelector(gitWriteLockSelector);

    const currentGitLockData = useMemo(
        () =>
            gitWriteLock.reduceRight((acc: GitLockData, currentGitLock: GitWriteLock) => {
                const currentId = currentGitLock.record.id;
                if (!acc[currentId]) {
                    acc[currentId] = currentGitLock;
                }
                return acc;
            }, {}),
        [gitWriteLock]
    );

    return currentGitLockData;
};
